// Data for the dynamic Products pages, served at /products/:slug via ProductPage.jsx
import {
  Cloud, Server, RefreshCw, Timer, ShieldCheck, Gauge,
  Brain, BarChart3, FileCheck2, Sparkles, Target, Eye,
  Code2, Bot, CheckCircle2, Terminal, Trophy, Zap,
} from "lucide-react";

export const PRODUCTS_DATA = {
  "cloud-labs": {
    label: "Cloud Labs",
    eyebrow: "CLOUD LABS",
    title: "Hands-on cloud labs, live in seconds",
    subtitle:
      "Real, sandboxed cloud environments for every learner — no shared accounts, no manual setup, no leftover cost. Launch a lab, run real commands, and let it auto-expire when the session ends.",
    whyMatters: [
      "Learners can't get real hands-on practice without expensive, hard-to-manage cloud accounts.",
      "Spinning up and tearing down lab environments manually eats instructor and IT time.",
      "Without guardrails, sandbox spend and security risk grow unchecked across a large cohort.",
    ],
    whatCards: [
      { icon: Server, title: "Sandboxed Environments", desc: "Isolated, real cloud infrastructure for every learner — never shared, never at risk." },
      { icon: Terminal, title: "Guided Labs", desc: "Step-by-step tasks with real commands, validated automatically as learners progress." },
      { icon: RefreshCw, title: "Auto Provision & Expiry", desc: "Environments spin up on demand and tear down automatically — zero manual cleanup." },
    ],
    capabilities: [
      { icon: Cloud, title: "Multi-cloud support", desc: "AWS, Azure, and Google Cloud labs from one catalogue." },
      { icon: Timer, title: "Time-boxed sessions", desc: "Sessions auto-expire on a schedule, keeping cost predictable." },
      { icon: ShieldCheck, title: "Isolated & secure", desc: "Every learner gets their own sandbox — no shared credentials." },
      { icon: Gauge, title: "Real infrastructure", desc: "Actual cloud consoles and CLIs, not simulations." },
    ],
    howSteps: [
      "Pick a lab from the catalogue.",
      "Launch a live, sandboxed cloud environment in seconds.",
      "Follow step-by-step guided tasks with real commands.",
      "Environment auto-expires — no cleanup, no leftover cost.",
    ],
    whoItems: [
      "L&D teams running hands-on cloud and DevOps training.",
      "Certification programs that need real infrastructure practice.",
      "IT teams who don't want to manage lab infrastructure themselves.",
    ],
    ctaHeadline: "Give every learner a real cloud environment, not a slideshow.",
  },

  "ai-powered-assessments": {
    label: "AI Powered Assessments",
    eyebrow: "AI POWERED ASSESSMENTS",
    title: "Assess real skill, graded instantly by AI",
    subtitle:
      "Coding, scenario-based, and written assessments — proctored, auto-graded, and scored against role-ready skill benchmarks. Get defensible skill data the moment a learner finishes.",
    whyMatters: [
      "Manual grading doesn't scale across hundreds or thousands of learners.",
      "Traditional MCQ-only tests don't reflect real, job-ready skill.",
      "Hiring and promotion decisions need objective, defensible skill data — not gut feel.",
    ],
    whatCards: [
      { icon: Brain, title: "AI-Graded Assessments", desc: "Coding, scenario, and written responses graded instantly and consistently." },
      { icon: Eye, title: "Proctored & Secure", desc: "AI-assisted proctoring and plagiarism detection keep results trustworthy." },
      { icon: BarChart3, title: "Skill Scoring & Analytics", desc: "Every result rolls up into role-based skill scores and gap reports." },
    ],
    capabilities: [
      { icon: FileCheck2, title: "Multiple question types", desc: "Coding, MCQ, and scenario-based questions in one assessment." },
      { icon: Sparkles, title: "Instant AI feedback", desc: "Learners see where they stood strong and where the gaps are." },
      { icon: Target, title: "Role-based benchmarks", desc: "Score against the bar for the role, not just a generic pass mark." },
      { icon: ShieldCheck, title: "Plagiarism detection", desc: "Automated similarity checks keep results honest." },
    ],
    howSteps: [
      "Choose or build an assessment from the question bank.",
      "Learners take proctored, timed assessments online.",
      "AI grades coding, scenario, and written responses instantly.",
      "Get skill scores and gap reports the moment it's done.",
    ],
    whoItems: [
      "L&D and hiring teams validating real skill, not just course completion.",
      "Certification bodies needing consistent, defensible grading.",
      "Managers benchmarking team readiness before deployment.",
    ],
    ctaHeadline: "Turn assessments into skill data you can actually act on.",
  },

  "ai-powered-coding-platform": {
    label: "AI Powered Coding Platform",
    eyebrow: "AI POWERED CODING PLATFORM",
    title: "A coding platform with an AI mentor built in",
    subtitle:
      "An in-browser IDE, real coding challenges, and an AI mentor that nudges learners forward with hints — never full answers — so they build the skill, not just copy a solution.",
    whyMatters: [
      "Switching between videos, docs, and a separate IDE breaks the learning flow.",
      "Learners get stuck with no one to ask, and give up before finishing.",
      "Manually reviewing thousands of code submissions isn't feasible for any team.",
    ],
    whatCards: [
      { icon: Code2, title: "In-Browser IDE", desc: "Write, run, and debug real code with zero setup, in every major language." },
      { icon: Bot, title: "AI Coding Mentor", desc: "Contextual hints when learners are stuck — guidance, not the answer." },
      { icon: CheckCircle2, title: "Auto-Evaluated Challenges", desc: "Submissions run against test cases and are scored instantly." },
    ],
    capabilities: [
      { icon: Terminal, title: "Multi-language support", desc: "Practice in the language your stack actually uses." },
      { icon: Zap, title: "Real-time hints", desc: "AI guidance that unblocks learners without giving away the solution." },
      { icon: ShieldCheck, title: "Similarity checks", desc: "Automated plagiarism detection across submissions." },
      { icon: Trophy, title: "Contests & hackathons", desc: "Run timed coding contests and leaderboards out of the box." },
    ],
    howSteps: [
      "Pick a coding challenge or track.",
      "Write and run code directly in the browser IDE.",
      "Get instant AI hints when you're stuck — no full answers given away.",
      "Submissions are auto-evaluated against test cases instantly.",
    ],
    whoItems: [
      "L&D teams running software engineering upskilling.",
      "Campus and early-career hiring teams screening coding skill.",
      "Communities running coding contests and hackathons.",
    ],
    ctaHeadline: "Give every learner a real IDE and a mentor that never sleeps.",
  },
};
